// Batches review viewport impressions and POSTs them to the backend
// `/widget/reviews/views` endpoint. Fire-and-forget: the backend dedupes per
// (review, visitor), applies a cooldown, and fires a PostHog event per
// impression. We mirror the like path's fetch conventions and reuse the same
// `getVisitorId()` so views and likes share one visitor identity.

import { getShopifyShopId } from "@/utils/metadata";
import { getVisitorId } from "@/utils/tracking";
import {
  ensurePostHogIdentity,
  readPostHogIdentitySync,
} from "@/utils/posthogIdentity";

const API_BASE_URL =
  import.meta.env.VITE_API_URL || "https://api.socialocean.co/api";
const VIEWS_URL = `${API_BASE_URL}/widget/reviews/views`;

const FLUSH_DEBOUNCE_MS = 1500;
const FLUSH_AT_SIZE = 20; // flush early once the buffer fills
const MAX_PER_REQUEST = 50; // backend hard cap (1–50 items per POST)
const MAX_VISITOR_ID = 64;
const MAX_INDEX = 10000;

interface PendingView {
  reviewId: number;
  index: number;
  topic: string | null;
}

// Module singletons (one closure per IIFE bundle instance). Keyed by
// `reviewId|topic` so a review seen under different filters counts once per
// filter, while an exact (review, filter) repeat is deduped.
const seen = new Set<string>();
const pending = new Map<string, PendingView>();
let flushTimer: ReturnType<typeof setTimeout> | undefined;
let listenersRegistered = false;

function abProps(): Record<string, unknown> {
  const ab = (window as any).__SO_AB__;
  if (!ab || (ab.variant !== "control" && ab.variant !== "treatment")) return {};
  return { ab_variant: ab.variant, experiment_key: ab.experiment, excluded: !!ab.excluded };
}

function keyFor(reviewId: number, topic: string | null): string {
  return `${reviewId}|${topic ?? ""}`;
}

/**
 * Record that a review scrolled into view, optionally under an active filter
 * chip (`topic`). Deduped per session by (review, filter) — first index wins —
 * and batched; call freely on every impression.
 */
export function queueReviewView(
  reviewId: number,
  index: number,
  topic: string | null = null,
): void {
  if (typeof window === "undefined") return;
  if (!Number.isInteger(reviewId) || reviewId <= 0) return;
  if (!Number.isInteger(index) || index < 0 || index > MAX_INDEX) return;

  const normalizedTopic = topic && topic !== "" ? topic : null;
  const key = keyFor(reviewId, normalizedTopic);
  if (seen.has(key) || pending.has(key)) return;

  pending.set(key, { reviewId, index, topic: normalizedTopic });
  ensureListeners();

  if (pending.size >= FLUSH_AT_SIZE) {
    void flush();
    return;
  }
  if (flushTimer === undefined) {
    flushTimer = setTimeout(() => void flush(), FLUSH_DEBOUNCE_MS);
  }
}

async function flush(useBeacon = false): Promise<void> {
  if (flushTimer !== undefined) {
    clearTimeout(flushTimer);
    flushTimer = undefined;
  }
  if (pending.size === 0) return;

  const visitorId = getVisitorId();
  if (!visitorId) {
    pending.clear();
    return;
  }

  // Drain synchronously so views queued during the (awaited) identity wait
  // belong to the next flush, and a re-observe can't re-queue these keys.
  const entries = Array.from(pending.entries());
  for (const [key] of entries) seen.add(key);
  const items = entries.map(([, view]) => view);
  pending.clear();

  // On the normal path wait (once) for buy-again's PostHog identity; on the
  // unload path read whatever is already there without awaiting.
  if (!useBeacon) await ensurePostHogIdentity();
  const identity = readPostHogIdentitySync();

  const base: Record<string, unknown> = {
    visitorId: visitorId.slice(0, MAX_VISITOR_ID),
    ...abProps(),
  };
  const shop = getShopifyShopId();
  if (shop) base.shop = shop;
  if (identity.distinctId) base.posthogDistinctId = identity.distinctId;
  if (identity.sessionId) base.posthogSessionId = identity.sessionId;
  if (identity.deviceId) base.posthogDeviceId = identity.deviceId;

  for (let i = 0; i < items.length; i += MAX_PER_REQUEST) {
    const views = items.slice(i, i + MAX_PER_REQUEST).map((v) =>
      v.topic
        ? { reviewId: v.reviewId, index: v.index, topic: v.topic }
        : { reviewId: v.reviewId, index: v.index },
    );
    send({ ...base, views }, useBeacon);
  }
}

function send(body: Record<string, unknown>, useBeacon: boolean): void {
  const payload = JSON.stringify(body);

  // On page-hide a normal fetch may not survive unload — prefer sendBeacon.
  if (
    useBeacon &&
    typeof navigator !== "undefined" &&
    typeof navigator.sendBeacon === "function"
  ) {
    try {
      // sendBeacon can't set headers; a typed Blob keeps the backend parsing JSON.
      const blob = new Blob([payload], { type: "application/json" });
      if (navigator.sendBeacon(VIEWS_URL, blob)) return;
    } catch {
      /* fall through to fetch */
    }
  }

  void fetch(VIEWS_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
    credentials: "omit",
    keepalive: true, // best-effort survival across an unload
    body: payload,
  }).catch(() => {
    /* fire-and-forget: analytics must never break the widget */
  });
}

function ensureListeners(): void {
  if (listenersRegistered || typeof window === "undefined") return;
  listenersRegistered = true;

  // pagehide is the most reliable terminal event (covers bfcache + unload).
  window.addEventListener("pagehide", () => void flush(true));
  // visibilitychange→hidden covers tab-switch / mobile app-switch.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flush(true);
  });
}
